import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    TextField, Grid, CircularProgress,
    Snackbar, Alert, MenuItem,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { clientService } from '../../../shared/services/clientService';
import { ClientFormData } from '../../../shared/types/client';
import { locationService } from '../../../shared/services/locationService';
import { LocationOption } from '../../../shared/types/location';

const EMPTY_FORM: ClientFormData = {
    companyName: '',
    ownerName: '',
    phone: '',
    email: '',
    address: '',
    country: '',
    state: '',
    city: '',
    gst: '',
};

export const AddEditClient: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isAdd = !id;

    const [form, setForm] = useState<ClientFormData>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<keyof ClientFormData, string>>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingClient, setIsLoadingClient] = useState(false);
    const [isLoadingCountries, setIsLoadingCountries] = useState(false);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });
    const [countries, setCountries] = useState<LocationOption[]>([]);
    const [states, setStates] = useState<LocationOption[]>([]);
    const [cities, setCities] = useState<LocationOption[]>([]);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [selectedStateId, setSelectedStateId] = useState('');
    const [selectedCityId, setSelectedCityId] = useState('');

    useEffect(() => {
        if (id) {
            loadClient(id);
        } else {
            loadCountries();
        }
    }, [id]);

    const findByName = (items: LocationOption[], name?: string): LocationOption | undefined => {
        if (!name) return undefined;
        return items.find((item) => item.name.trim().toLowerCase() === name.trim().toLowerCase());
    };

    const loadCountries = async (): Promise<LocationOption[]> => {
        setIsLoadingCountries(true);
        try {
            const list = await locationService.getCountries();
            setCountries(list);
            return list;
        } catch (err: any) {
            setAlert({
                show: true,
                type: 'error',
                message: err.response?.data?.message || 'Failed to load countries',
            });
            return [];
        } finally {
            setIsLoadingCountries(false);
        }
    };

    const loadStates = async (countryId: string): Promise<LocationOption[]> => {
        if (!countryId) {
            setStates([]);
            return [];
        }

        setIsLoadingStates(true);
        try {
            const list = await locationService.getStatesByCountry(countryId);
            setStates(list);
            return list;
        } catch (err: any) {
            setAlert({
                show: true,
                type: 'error',
                message: err.response?.data?.message || 'Failed to load states',
            });
            return [];
        } finally {
            setIsLoadingStates(false);
        }
    };

    const loadCities = async (stateId: string): Promise<LocationOption[]> => {
        if (!stateId) {
            setCities([]);
            return [];
        }

        setIsLoadingCities(true);
        try {
            const list = await locationService.getCitiesByState(stateId);
            setCities(list);
            return list;
        } catch (err: any) {
            setAlert({
                show: true,
                type: 'error',
                message: err.response?.data?.message || 'Failed to load cities',
            });
            return [];
        } finally {
            setIsLoadingCities(false);
        }
    };

    const hydrateLocationSelection = async (countryName: string, stateName: string, cityName: string) => {
        const countryList = await loadCountries();
        const matchedCountry = findByName(countryList, countryName);
        if (!matchedCountry) return;

        setSelectedCountryId(matchedCountry.id);

        const stateList = await loadStates(matchedCountry.id);
        const matchedState = findByName(stateList, stateName);
        if (!matchedState) return;

        setSelectedStateId(matchedState.id);

        const cityList = await loadCities(matchedState.id);
        const matchedCity = findByName(cityList, cityName);
        if (matchedCity) {
            setSelectedCityId(matchedCity.id);
        }
    };

    const loadClient = async (clientId: string) => {
        setIsLoadingClient(true);
        try {
            const res = await clientService.getById(clientId);
            const client = res.data?.client || res.data?.result || (res as any)?.data;
            if (client) {
                setForm({
                    companyName: client.companyName || '',
                    ownerName: client.ownerName || '',
                    phone: client.phone || '',
                    email: client.email || '',
                    address: client.address || '',
                    country: client.country || '',
                    state: client.state || '',
                    city: client.city || '',
                    gst: client.gst || '',
                });

                await hydrateLocationSelection(
                    client.country || '',
                    client.state || '',
                    client.city || '',
                );
            }
        } catch (err: any) {
            setAlert({
                show: true, type: 'error',
                message: err.response?.data?.message || 'Error loading client details',
            });
        } finally {
            setIsLoadingClient(false);
        }
    };

    const validate = (): boolean => {
        const newErrors: Partial<Record<keyof ClientFormData, string>> = {};

        const hasCompany = !!form.companyName?.trim();
        const hasOwner = !!form.ownerName?.trim();

        if (!hasCompany && !hasOwner) {
            newErrors.companyName = 'At least company or owner name is required';
            newErrors.ownerName = 'At least company or owner name is required';
        } else {
            if (hasCompany && form.companyName!.trim().length < 2) {
                newErrors.companyName = 'Min 2 characters';
            }
            if (hasOwner && form.ownerName!.trim().length < 2) {
                newErrors.ownerName = 'Min 2 characters';
            }
        }

        if (!form.address.trim()) newErrors.address = 'Address is required';
        if (!form.country.trim()) newErrors.country = 'Country is required';
        if (!form.state.trim()) newErrors.state = 'State is required';
        if (!form.city.trim()) newErrors.city = 'City is required';

        if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const set = (key: keyof ClientFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(f => ({ ...f, [key]: e.target.value }));
        if (errors[key]) setErrors(err => ({ ...err, [key]: '' }));
    };

    const handleCountryChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const countryId = e.target.value;
        const country = countries.find((item) => item.id === countryId);

        setSelectedCountryId(countryId);
        setSelectedStateId('');
        setSelectedCityId('');
        setStates([]);
        setCities([]);

        setForm((prev) => ({
            ...prev,
            country: country?.name || '',
            state: '',
            city: '',
        }));

        setErrors((prev) => ({ ...prev, country: '', state: '', city: '' }));

        if (countryId) {
            await loadStates(countryId);
        }
    };

    const handleStateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const stateId = e.target.value;
        const state = states.find((item) => item.id === stateId);

        setSelectedStateId(stateId);
        setSelectedCityId('');
        setCities([]);

        setForm((prev) => ({
            ...prev,
            state: state?.name || '',
            city: '',
        }));

        setErrors((prev) => ({ ...prev, state: '', city: '' }));

        if (stateId) {
            await loadCities(stateId);
        }
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cityId = e.target.value;
        const city = cities.find((item) => item.id === cityId);

        setSelectedCityId(cityId);
        setForm((prev) => ({
            ...prev,
            city: city?.name || '',
        }));
        setErrors((prev) => ({ ...prev, city: '' }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        try {
            // Optional fields: omit entirely if empty so backend doesn't flag them as required
            const payload: ClientFormData = {
                ...form,
                ...(form.phone?.trim() ? { phone: form.phone.trim() } : { phone: undefined }),
                ...(form.email?.trim() ? { email: form.email.trim() } : { email: undefined }),
                ...(form.gst?.trim() ? { gst: form.gst.trim() } : { gst: undefined }),
            };

            if (isAdd) {
                await clientService.create(payload);
            } else {
                await clientService.update(id!, payload);
            }

            setAlert({
                show: true, type: 'success',
                message: isAdd ? 'Client created successfully' : 'Client updated successfully',
            });
            setTimeout(() => navigate('/clients'), 1000);
        } catch (err: any) {
            const responseData = err.response?.data;

            // Parse field-specific validation errors from API
            if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                // Map API field errors to form field errors
                const apiErrors: Partial<Record<keyof ClientFormData, string>> = {};
                responseData.errors.forEach((e: { field: string; message: string }) => {
                    const field = e.field as keyof ClientFormData;
                    // Only set the first error per field
                    if (!apiErrors[field]) {
                        apiErrors[field] = e.message;
                    }
                });
                setErrors(prev => ({ ...prev, ...apiErrors }));

                // Build a readable alert message from unique error messages
                const uniqueMessages = [...new Set(responseData.errors.map((e: { message: string }) => e.message))];
                setAlert({
                    show: true,
                    type: 'error',
                    message: uniqueMessages.join(' • '),
                });
            } else {
                const msg = responseData?.message || err.message || 'An error occurred';
                setAlert({ show: true, type: 'error', message: msg });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: 'transparent', border: '1px solid #6366f1' },
        },
        '& .MuiInputBase-input': { color: '#1f2937', fontSize: '15px' },
    };

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-5xl">

                    {/* Back link */}
                    <div className="-ml-4 mb-2">
                        <Link to="/clients" className="back-link">
                            <ArrowBack className="text-[#6366f1]" />
                            <span className="ml-2 text-[#6366f1]">Back to Clients</span>
                        </Link>
                    </div>

                    <div className="title text-2xl sm:text-5xl">
                        {isAdd ? 'Add Client' : 'Edit Client'}
                    </div>

                    <div className="form-card mt-8">
                        {isLoadingClient ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <CircularProgress size={50} sx={{ color: '#6366f1' }} />
                                <p className="mt-4 text-gray-600 font-medium">Loading client details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert
                                        severity={alert.type}
                                        className="mb-8 rounded-xl"
                                        onClose={() => setAlert(a => ({ ...a, show: false }))}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Section: Company Info */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-[#6366f1] mb-2">
                                            Company Information
                                        </p>
                                        <p className="text-[11px] text-gray-500 mb-4">* At least one of Company Name or Owner Name is required</p>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Company Name
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    value={form.companyName}
                                                    onChange={set('companyName')}
                                                    error={!!errors.companyName}
                                                    helperText={errors.companyName}
                                                    placeholder="Enter company name"
                                                    sx={fieldStyles}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Owner Name
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    value={form.ownerName}
                                                    onChange={set('ownerName')}
                                                    error={!!errors.ownerName}
                                                    helperText={errors.ownerName}
                                                    placeholder="Enter owner name"
                                                    sx={fieldStyles}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Email
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    type="email"
                                                    value={form.email}
                                                    onChange={set('email')}
                                                    error={!!errors.email}
                                                    helperText={errors.email}
                                                    placeholder="Enter email address"
                                                    sx={fieldStyles}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Phone
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    value={form.phone}
                                                    onChange={set('phone')}
                                                    error={!!errors.phone}
                                                    helperText={errors.phone}
                                                    placeholder="Enter phone number"
                                                    sx={fieldStyles}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    GST Number
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    value={form.gst}
                                                    onChange={set('gst')}
                                                    placeholder="Enter GST number (optional)"
                                                    sx={fieldStyles}
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {/* Section: Address */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-[#6366f1] mb-4">
                                            Address Details
                                        </p>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Address <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    fullWidth required
                                                    value={form.address}
                                                    onChange={set('address')}
                                                    error={!!errors.address}
                                                    helperText={errors.address}
                                                    placeholder="Street address"
                                                    multiline rows={2}
                                                    sx={{
                                                        ...fieldStyles,
                                                        '& .MuiOutlinedInput-root': {
                                                            ...fieldStyles['& .MuiOutlinedInput-root'],
                                                            height: 'auto',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Country <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    required
                                                    value={selectedCountryId}
                                                    onChange={handleCountryChange}
                                                    error={!!errors.country}
                                                    helperText={errors.country}
                                                    disabled={isLoadingCountries}
                                                    SelectProps={{ displayEmpty: true }}
                                                    sx={fieldStyles}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {isLoadingCountries ? 'Loading countries...' : 'Select country'}
                                                    </MenuItem>
                                                    {countries.map((country) => (
                                                        <MenuItem key={country.id} value={country.id}>
                                                            {country.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    State <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    required
                                                    value={selectedStateId}
                                                    onChange={handleStateChange}
                                                    error={!!errors.state}
                                                    helperText={errors.state}
                                                    disabled={!selectedCountryId || isLoadingStates}
                                                    SelectProps={{ displayEmpty: true }}
                                                    sx={fieldStyles}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {!selectedCountryId
                                                            ? 'Select country first'
                                                            : isLoadingStates
                                                                ? 'Loading states...'
                                                                : 'Select state'}
                                                    </MenuItem>
                                                    {states.map((state) => (
                                                        <MenuItem key={state.id} value={state.id}>
                                                            {state.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    City <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    required
                                                    value={selectedCityId}
                                                    onChange={handleCityChange}
                                                    error={!!errors.city}
                                                    helperText={errors.city}
                                                    disabled={!selectedStateId || isLoadingCities}
                                                    SelectProps={{ displayEmpty: true }}
                                                    sx={fieldStyles}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {!selectedStateId
                                                            ? 'Select state first'
                                                            : isLoadingCities
                                                                ? 'Loading cities...'
                                                                : 'Select city'}
                                                    </MenuItem>
                                                    {cities.map((city) => (
                                                        <MenuItem key={city.id} value={city.id}>
                                                            {city.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate('/clients')}
                                            sx={{ px: 4 }}
                                        >
                                            Cancel
                                        </AppButton>
                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            startIcon={<SaveIcon />}
                                            loading={isLoading}
                                            sx={{ px: 5 }}
                                        >
                                            {isAdd ? 'Create Client' : 'Update Client'}
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert(a => ({ ...a, show: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled" className="rounded-xl">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
